import { useEffect, useState, useRef } from "react";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";
import { Loader2, Pencil, Save } from "lucide-react";

export default function Account() {
    const [form, setForm] = useState({
        username: "",
        first_name: "",
        last_name: "",
        password: "",
    });

    const [originalData, setOriginalData] = useState({});
    const [editable, setEditable] = useState({
        username: false,
        first_name: false,
        last_name: false,
        password: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEdited, setIsEdited] = useState(false);

    const inputRefs = {
        username: useRef(null),
        first_name: useRef(null),
        last_name: useRef(null),
        password: useRef(null),
    };

    const fetchAdmin = async () => {
        try {
            const res = await axiosInstance.get("/api/admin/get-all-admin");
            const data = res.data?.data?.[0];
            if (data) {
                const formatted = {
                    username: data.Username || "",
                    first_name: data.FirstName || "",
                    last_name: data.LastName || "",
                    password: "",
                };
                setForm(formatted);
                setOriginalData(formatted);
            } else toast.error("ادمینی یافت نشد");
        } catch {
            toast.error("خطا در دریافت اطلاعات ادمین");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmin();
    }, []);

    const checkIfEdited = (updatedForm) => {
        const hasChanges = Object.keys(updatedForm).some((key) => {
            if (key === "password") return updatedForm[key] !== "";
            return updatedForm[key] !== originalData[key];
        });
        setIsEdited(hasChanges);
    };

    const handleChange = (field, value) => {
        setForm((prev) => {
            const updated = { ...prev, [field]: value };
            checkIfEdited(updated);
            return updated;
        });
    };

    const toggleEdit = (field) => {
        setEditable((prev) => {
            const nowEditing = !prev[field];

            if (nowEditing) {
                setTimeout(() => {
                    inputRefs[field]?.current?.focus();
                }, 50);
            } else {
                setForm((prev) => {
                    const reverted = { ...prev, [field]: originalData[field] || "" };
                    checkIfEdited(reverted);
                    return reverted;
                });
            }

            return { ...prev, [field]: nowEditing };
        });
    };

    const validateFields = (data) => {
        for (const key in data) {
            const value = data[key];
            if (key === "password") {
                if (value && (value.length < 6 || value.length > 20)) {
                    toast.error("رمز عبور باید بین ۶ تا ۲۰ کاراکتر باشد");
                    return false;
                }
            } else {
                if (value.length < 3) {
                    toast.error(`فیلد ${key} باید حداقل ۳ کاراکتر باشد`);
                    return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const changedFields = {};
        Object.keys(form).forEach((key) => {
            if (key === "password") {
                if (form.password.trim() !== "") changedFields.password = form.password;
            } else if (form[key] !== originalData[key]) {
                changedFields[key] = form[key];
            }
        });

        if (Object.keys(changedFields).length === 0) {
            toast("هیچ تغییری ایجاد نشده است");
            return;
        }

        if (!validateFields(changedFields)) return;

        setSaving(true);
        try {
            await axiosInstance.patch("/api/admin/edit-profile-admin", changedFields);
            toast.success("تغییرات با موفقیت ذخیره شد");

            setEditable({
                username: false,
                first_name: false,
                last_name: false,
                password: false,
            });

            setOriginalData((prev) => ({ ...prev, ...changedFields }));
            setForm((prev) => ({ ...prev, password: "" }));
            setIsEdited(false);
        } catch {
            toast.error("خطا در بروزرسانی اطلاعات");
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return (
            <div className="flex justify-center items-center gap-1.5 h-[60vh] text-background">
                <Loader2 strokeWidth={1.5} className="animate-spin mr-2 text-black/70" />
                در حال دریافت اطلاعات...
            </div>
        );

    return (
        <section className="w-full max-w-4xl mx-auto flex flex-col gap-6">
            <h2 className="text-xl font-semibold text-background py-3">
                حساب کاربری ادمین
            </h2>

            <form
                onSubmit={handleSubmit}
                className={`flex flex-col gap-6 bg-white/50 p-6 rounded-xl border border-black/5 transition ${saving ? "opacity-50 pointer-events-none" : ""
                    }`}
            >
                {Object.entries({
                    username: "نام کاربری",
                    first_name: "نام",
                    last_name: "نام خانوادگی",
                    password: "رمز عبور جدید (اختیاری)",
                }).map(([key, label]) => (
                    <div key={key} className="relative">
                        <label className="text-sm font-medium text-background">
                            {label}
                        </label>
                        <input
                            ref={inputRefs[key]}
                            type={key === "password" ? "password" : "text"}
                            value={form[key]}
                            onChange={(e) => handleChange(key, e.target.value)}
                            disabled={!editable[key]}
                            placeholder={label}
                            maxLength={key === "password" ? 20 : 100}
                            className={`w-full h-11 mt-1 px-4 py-2 rounded-lg border bg-white/50 text-background outline-none transition ${editable[key]
                                ? "border-accent focus:border-accent"
                                : "border-black/5 cursor-not-allowed text-black/60"
                                }`}
                        />
                        <button
                            type="button"
                            onClick={() => toggleEdit(key)}
                            className="absolute left-2 bottom-2 text-accent hover:bg-orange-100 p-1.5 rounded-md transition"
                        >
                            <Pencil size={16} />
                        </button>
                    </div>
                ))}

                {isEdited && (
                    <button
                        type="submit"
                        disabled={saving}
                        className="bg-accent text-foreground px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-accent transition-colors"
                    >
                        {saving && <Loader2 className="animate-spin" size={18} />}
                        {saving ? "در حال ذخیره..." : "ذخیره تغییرات"}
                        {!saving && <Save size={18} />}
                    </button>
                )}
            </form>
        </section>
    );
}